import { FormControl, FormGroup } from '@angular/forms';
import { ControlType } from './control-type.enum';

// Stored representations that mean "on" for a checkbox/toggle submission value.
// Mirrors the truthy set the API maps to "Yes" when it builds the SBRA export,
// so the UI and the submitted spreadsheet always agree.
const TRUTHY_STORED_VALUES = ['true', '1', 'yes', 'y'];

//
export class DynamicFormControl {
  controlType?: ControlType;
  customOptions?: any;
  label?: string;
  propertyBinding?: string;
  defaultValue?: any;
  tooltip?: string;
  color?: 'primary' | 'secondary' | 'warn' = 'primary';
  errorMessage?: string;
  warningMessage?: string;
  warningTooltip?: string;
  hintText?: string;

  //Control behavior options
  isRequired?: boolean;
  hidden?: boolean = false;
  touched?: boolean = false;
  readonly?: boolean = false;
  tooltipHidden?: boolean = false;
  warningTooltipHidden?: boolean = false;

  formControls?: { propertyBinding?: string; formControl?: FormControl }[];

  markAsTouched() {
    this.touched = true;
  }
  markAsReadOnly() {
    this.readonly = true;
  }
  markAsPristine() {
    this.touched = false;
  }
  hideControl() {
    this.hidden = true;
  }
  showControl() {
    this.hidden = false;
  }

  showTooltip() {
    this.tooltipHidden = false;
  }

  hideTooltip() {
    this.tooltipHidden = true;
  }

  setTooltip(value: string) {
    this.tooltip = value;
  }

  setError(value: string) {
    this.errorMessage = value;
  }

  setWarning(value: string) {
    this.warningMessage = value;
  }

  setHintText(value: string) {
    this.hintText = value;
  }

  showWarningTooltip() {
    this.warningTooltipHidden = false;
  }

  hideWarningTooltip() {
    this.warningTooltipHidden = true;
  }

  setWarningTooltip(value: string) {
    this.warningTooltip = value;
  }

  addFormControl(propertyBinding: string, value?: any) {
    this.formControls = [
      ...this.formControls,
      {
        propertyBinding: propertyBinding,
        formControl: new FormControl(value),
      },
    ];
  }

  /**
   * Sets the value of the control.
   * @param value Value of the control.
   * @param propertyBinding Used to set the value of a control that is not named the same as the dynamic control.
   * @param form Used to patch the value of a select control
   */
  setValue(value: any, propertyBinding?: string, form?: FormGroup) {
    let ctrl = this.formControls.find(
      (i) =>
        i.propertyBinding.toLowerCase() === this.propertyBinding.toLowerCase(),
    );

    if (propertyBinding) {
      ctrl = this.formControls.find(
        (i) => i.propertyBinding === propertyBinding,
      );
    }

    switch (this.controlType) {
      case ControlType.DATE:
        if (!value) {
          ctrl.formControl.setValue(null, { emitEvent: false });
        } else {
          const parsedDate = value instanceof Date ? value : new Date(value);
          ctrl.formControl.setValue(
            isNaN(parsedDate.getTime()) ? null : parsedDate,
            {
              emitEvent: false,
            },
          );
        }

        break;
      case ControlType.CHECKBOX:
      case ControlType.TOGGLE:
        // Only an explicitly affirmative value turns the control on. Anything
        // else - null, an empty string (the API coalesces a null submission
        // value to ""), "false" - is off. Treating "not false" as on flipped
        // untouched toggles (Certificate Mailed?, Basement?, Attached Home?)
        // on once the submission had been saved with a null value for them.
        ctrl.formControl.setValue(
          TRUTHY_STORED_VALUES.includes(`${value}`.trim().toLowerCase()),
          { onlySelf: true, emitEvent: false },
        );
        break;
      case ControlType.SELECT:
        let patchValue: any = {};
        (patchValue as any)[ctrl.propertyBinding] = value;
        if (
          ctrl.propertyBinding === 'isHeatPumpDocumentOnFile' &&
          value !== null
        ) {
          (patchValue as any)[ctrl.propertyBinding] =
            value === 'true' ? true : false;
        }
        form?.patchValue(patchValue, { emitEvent: false });
        break;
      case ControlType.INCREMENTING:
        break;
      case ControlType.SBRA_DROPDOWN:
        let sbraValue: any = {};
        (sbraValue as any)[ctrl.propertyBinding] = value;
        form?.patchValue(sbraValue, { emitEvent: false });
        break;
      default:
        ctrl.formControl.setValue(value, { emitEvent: false });
        break;
    }
  }
}
